import { useEffect, useMemo, useRef, useState } from 'react';
import { lookupCep } from './useCepSearch';
import { compareAddressWithCep, type AddressDivergence, type CepAddress } from '../utils/addressCepCheck';

export type AddressCepStatus = 'idle' | 'loading' | 'ok' | 'notFound' | 'unavailable';

export interface AddressCepCheckInput extends CepAddress {
  zip: string;
}

export interface UseAddressCepCheckOptions {
  /** false desliga a consulta (ex.: pedido encerrado, formulário fechado). */
  enabled?: boolean;
  /** Chamado uma vez por consulta bem-sucedida — para preencher campos vazios. */
  onFound?: (cep: CepAddress) => void;
  debounceMs?: number;
}

export interface UseAddressCepCheckResult {
  status: AddressCepStatus;
  /** Endereço do CEP consultado (quando status = ok). */
  cepAddress: CepAddress | null;
  /** CEP (8 dígitos) a que o resultado se refere. */
  checkedZip: string;
  divergences: AddressDivergence[];
}

/**
 * Consulta o CEP automaticamente quando ele chega a 8 dígitos e compara o endereço digitado
 * com o da base de CEP. Mudanças em rua/bairro/cidade/UF reavaliam a comparação sem nova
 * consulta. Respostas atrasadas de um CEP anterior são descartadas.
 */
export function useAddressCepCheck(
  address: AddressCepCheckInput,
  { enabled = true, onFound, debounceMs = 400 }: UseAddressCepCheckOptions = {},
): UseAddressCepCheckResult {
  const zip = (address.zip ?? '').replace(/\D/g, '');
  const [status, setStatus] = useState<AddressCepStatus>('idle');
  const [cepAddress, setCepAddress] = useState<CepAddress | null>(null);
  const [checkedZip, setCheckedZip] = useState('');
  const onFoundRef = useRef(onFound);
  onFoundRef.current = onFound;

  useEffect(() => {
    if (!enabled || zip.length !== 8) {
      setStatus('idle');
      setCepAddress(null);
      setCheckedZip('');
      return;
    }
    let cancelled = false;
    setStatus('loading');
    const timer = setTimeout(async () => {
      const outcome = await lookupCep(zip);
      if (cancelled) return;
      setCheckedZip(zip);
      if (outcome.status === 'ok') {
        const found: CepAddress = {
          uf: outcome.data.uf,
          city: outcome.data.localidade,
          district: outcome.data.bairro,
          street: outcome.data.logradouro,
        };
        setCepAddress(found);
        setStatus('ok');
        onFoundRef.current?.(found);
      } else {
        setCepAddress(null);
        setStatus(outcome.status === 'notFound' ? 'notFound' : 'unavailable');
      }
    }, debounceMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [zip, enabled, debounceMs]);

  const divergences = useMemo(
    () => (status === 'ok' && cepAddress
      ? compareAddressWithCep(
        { uf: address.uf, city: address.city, district: address.district, street: address.street },
        cepAddress,
      )
      : []),
    [status, cepAddress, address.uf, address.city, address.district, address.street],
  );

  return { status, cepAddress, checkedZip, divergences };
}
